<template>
  <Modal
    contentWidth="750px"
    :required="true"
    @close="$emit('close')"
  >
    <template #header>
      <p class="fs-20 font-weight-bold ml-1 mb-0" style="line-height: 0.675rem; color: #163D7D;">
        Solicitar plano
      </p>
    </template>

    <template #body>
      <ValidationObserver v-slot="{ invalid }">
        <b-overlay :show="confirmIncomplete" blur="3px" opacity="0.6" variant="white" rounded="lg">
          <template #overlay>
            <div class="bg-white shadow-lg rounded-lg p-3 text-left" style="width: 460px; max-width: 100%;">
              <p class="font-weight-bold text-dark mb-2">
                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="text-warning" fixed-width />
                Submitals incompletos
              </p>
              <p class="fs-14 mb-2">
                Los siguientes submitals seleccionados están incompletos. La solicitud podría ser rechazada debido a esto
              </p>
              <ul class="fs-13 bg-light rounded-lg py-2 pl-4 pr-2 mb-2 overflow-auto" style="max-height: 120px;">
                <li v-for="item in incompleteSelectedItems" :key="itemKey(item)">
                  <span class="font-weight-bold">{{ item.submital_no }}</span>
                </li>
              </ul>
              <p class="font-weight-bold text-center mb-3">¿Desea continuar?</p>
              <div class="text-center">
                <Button variant="orange" class="mr-2" style="width: 95px;" :disabled="invalid || processing" @click="createDrawingRequest">
                  <b-spinner v-if="processing" small class="mr-1"></b-spinner>
                  Sí, solicitar
                </Button>
                <Button variant="blue" style="width: 95px;" :disabled="processing" @click="confirmIncomplete = false">
                  No
                </Button>
              </div>
              <p v-if="errorMessage" class="text-danger text-center fs-13 mt-2 mb-0">{{ errorMessage }}</p>
            </div>
          </template>

          <b-row class="mb-2">
            <b-col md="5">
              <b-form-group label="CL" label-cols="3" label-cols-md="5" label-class="text-muted" class="mb-2">
                <b-input :value="clientNum" disabled size="sm" />
              </b-form-group>
            </b-col>
            <b-col md="7">
              <b-form-group label="Solicitante" label-cols="3" label-class="text-muted" class="mb-0">
                <b-input :value="requesterName" disabled size="sm" />
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Cliente" label-cols-md="2" label-class="text-muted" class="mb-2">
            <b-input :value="clientName" disabled size="sm" />
          </b-form-group>

          <!-- Documentos asociados -->
          <b-form-group label-cols-md="2" label-class="text-muted" class="mb-2">
            <template #label>
              Documentos<span class="asterisk">*</span>
            </template>

            <b-row class="align-items-center mb-1 mt-2">
              <b-col cols="4" md="5">
                <b-checkbox v-model="includeOrder">Orden de instalación</b-checkbox>
              </b-col>
              <b-col cols="8" md="7">
                <ValidationProvider v-if="includeOrder" rules="required" name="Orden de instalación" v-slot="{ errors }">
                  <b-input
                    v-model="form.installation_order_number"
                    type="number"
                    min="1"
                    size="sm"
                    :state="errors[0] ? false : null"
                  />
                </ValidationProvider>
              </b-col>
            </b-row>

            <b-row class="align-items-center mb-1">
              <b-col cols="4" md="5">
                <b-checkbox v-model="includeQuotation">Cotización</b-checkbox>
              </b-col>
              <b-col cols="8" md="7">
                <ValidationProvider v-if="includeQuotation" rules="required" name="Cotización" v-slot="{ errors }">
                  <b-input
                    v-model="form.quotation_number"
                    type="number"
                    min="1"
                    size="sm"
                    :state="errors[0] ? false : null"
                  />
                </ValidationProvider>
              </b-col>
            </b-row>

            <b-row class="align-items-center mb-1">
              <b-col>
                <b-checkbox v-model="isPresale">
                  Preventa
                </b-checkbox>
              </b-col>
            </b-row>
          </b-form-group>

          <!-- Submitals a relacionar -->
          <div class="mb-3">
            <p class="text-muted mb-1">
              Submitals a relacionar<span class="asterisk">*</span>
            </p>
            <div class="items-box">
              <div v-if="itemsLoading" class="text-center text-info py-4 fs-15">
                <b-spinner small type="grow"></b-spinner>
                <span class="pl-2">Cargando submitals del proyecto...</span>
              </div>

              <div v-else-if="itemsError" class="text-center text-danger py-4 fs-15">
                No se pudieron cargar los submitals<br>
                <b-link class="pl-1" @click="fetchItems">Reintentar</b-link>
              </div>

              <div v-else-if="items.length === 0" class="text-center py-4 fs-15 text-info">
                <font-awesome-icon :icon="['far', 'info-circle']" size="lg" fixed-width />
                Se requiere al menos un submital para poder crear la solicitud
              </div>

              <div
                v-for="item in items"
                v-else
                :key="itemKey(item)"
                class="item-row d-flex align-items-center"
              >
                <div class="item-check">
                  <b-checkbox v-model="selectedItems" :value="itemKey(item)" class="mb-0" />
                </div>

                <span class="item-number fs-13">{{ item.submital_no }}</span>

                <span class="item-title fs-13 text-truncate" :class="{ 'text-muted': !item.name }">
                  {{ item.name || 'Sin nombre' }}
                </span>

                <span v-b-tooltip.hover :title="photosTooltip(item)" class="item-photos fs-12" :class="photosClass(item)">
                  <font-awesome-icon :icon="['far', 'image']" size="lg" fixed-width />
                  <span class="item-count">{{ item.completed_photos_count }}/{{ item.required_photos_count }}</span>
                </span>

                <span class="item-progress fs-13" :class="item.progress >= 100 ? 'text-success' : 'text-danger'">
                  <span class="item-count">{{ item.progress }}%</span>
                  <font-awesome-icon
                    :icon="item.progress >= 100 ? ['fas', 'shield-check'] : ['fas', 'exclamation-triangle']"
                    :class="item.progress >= 100 ? 'text-success' : 'text-warning'"
                    fixed-width
                  />
                </span>
              </div>
            </div>
          </div>

          <ValidationProvider rules="required" name="Tipo de plano" v-slot="{ errors }">
            <b-form-group label-cols-md="3" label-class="text-muted" :invalid-feedback="errors[0]">
              <template #label>
                Tipo de plano<span class="asterisk">*</span>
              </template>
              <b-select
                v-model="form.drawing_type"
                :options="drawingTypeOptions"
                size="sm"
                :state="errors[0] ? false : null"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider :rules="{ required: true, after_date: minDateRequired }" name="Fecha requerida" :custom-messages="{ after_date: 'La fecha requerida debe ser hoy o posterior' }" v-slot="{ errors }">
            <b-form-group label-cols-md="3" label-class="text-muted" :invalid-feedback="errors[0]">
              <template #label>
                Fecha requerida<span class="asterisk">*</span>
              </template>
              <b-input
                v-model="form.required_date"
                type="date"
                :min="today"
                size="sm"
                :state="errors[0] ? false : null"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider :rules="{ required: true, min: 14 }" name="Título" v-slot="{ errors }">
            <b-form-group label-cols-md="3" label-class="text-muted" :invalid-feedback="errors[0]">
              <template #label>
                Título<span class="asterisk">*</span>
              </template>
              <b-input
                v-model="form.title"
                size="sm"
                :state="errors[0] ? false : null"
              />
            </b-form-group>
          </ValidationProvider>

          <b-form-group label="Comentarios" label-cols-md="3" label-class="text-muted">
            <template #label>
              Comentarios<sup class="text-secondary ml-1 fs-12 font-italic">(Opcional)</sup>
            </template>
            <b-textarea v-model="form.description" rows="2" size="sm" />
          </b-form-group>

          <div class="mt-3 mb-1">
            <div class="text-center">
              <Button
                variant="orange"
                :disabled="invalid || !documentsValid || selectedItems.length === 0 || processing || confirmIncomplete"
                @click="onSubmitClick"
              >
                <b-spinner v-if="processing" small class="mr-1"></b-spinner>
                Solicitar
              </Button>
            </div>

            <p v-if="errorMessage && !confirmIncomplete" class="text-danger text-center fs-13 mt-2 mb-0">{{ errorMessage }}</p>
          </div>
        </b-overlay>
      </ValidationObserver>
    </template>

    <template #footer>
      <span></span>
    </template>
  </Modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import Modal from '../../shared/Modal'
import Button from '../../shared/Button'
import { drawingTypeOptions } from '../../../sharedOptions/drawingRequestOptions'

export default {
  name: 'DrawingRequestFormModal',
  components: { ValidationObserver, ValidationProvider, Modal, Button },
  props: {
    project: { type: Object, required: true }
  },
  data () {
    return {
      form: {
        title: null,
        description: null,
        drawing_type: null,
        required_date: null,
        installation_order_number: null,
        quotation_number: null
      },
      includeOrder: false,
      includeQuotation: false,
      isPresale: false,
      items: [],
      selectedItems: [],
      itemsLoading: false,
      itemsError: false,
      processing: false,
      errorMessage: null,
      confirmIncomplete: false
    }
  },
  watch: {
    isPresale (value) {
      if (value) {
        this.includeOrder = false
        this.includeQuotation = false
      }
    },
    includeOrder (value) {
      if (value) this.isPresale = false
      else this.form.installation_order_number = null
    },
    includeQuotation (value) {
      if (value) this.isPresale = false
      else this.form.quotation_number = null
    },
    selectedItems () {
      this.confirmIncomplete = false
    }
  },
  mounted () {
    this.fetchItems()
  },
  computed: {
    currentUser () {
      return this.$store.state.currentUser
    },
    requesterName () {
      return this.currentUser ? `${this.currentUser.first_name} ${this.currentUser.last_name}` : ''
    },
    clientNum () {
      return this.project.client ? this.project.client.client_num : ''
    },
    clientName () {
      return this.project.client ? this.project.client.name : 'Sin cliente'
    },
    drawingTypeOptions () {
      return [{ value: null, text: '- Seleccione -', disabled: true }, ...drawingTypeOptions]
    },
    today () {
      const now = new Date()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      return `${now.getFullYear()}-${month}-${day}`
    },
    minDateRequired() {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)

      const month = String(yesterday.getMonth() + 1).padStart(2, '0')
      const day = String(yesterday.getDate()).padStart(2, '0')
      return `${yesterday.getFullYear()}-${month}-${day}`
    },
    documentsValid () {
      return this.isPresale || this.includeOrder || this.includeQuotation
    },
    associatedDocuments () {
      if (this.isPresale) return ['preventa']

      const docs = []
      if (this.includeOrder) docs.push('orden_instalacion')
      if (this.includeQuotation) docs.push('cotizacion')
      return docs
    },
    incompleteSelectedItems () {
      return this.items.filter(item =>
        this.selectedItems.includes(this.itemKey(item)) && this.isIncomplete(item)
      )
    }
  },
  methods: {
    fetchItems () {
      this.itemsLoading = true
      this.itemsError = false

      this.$http.get('/api/production/drawing_requests/available_items', {
        params: { project_id: this.project.id }
      })
        .then((res) => {
          this.items = res.data.items
        })
        .catch(() => {
          this.itemsError = true
        })
        .finally(() => {
          this.itemsLoading = false
        })
    },
    itemKey (item) {
      return `${item.itemable_type}:${item.itemable_id}`
    },
    createDrawingRequest () {
      if (this.processing) return
      this.processing = true
      this.errorMessage = null

      this.$http.post('/api/production/drawing_requests', { drawing_request: this.formatPayload() })
        .then((res) => {
          this.$emit('created', res.data)
        })
        .catch((error) => {
          const message = error.response && error.response.data && error.response.data.error
          this.errorMessage = message || 'No se pudo crear la solicitud'
        })
        .finally(() => {
          this.processing = false
        })
    },
    formatPayload () {
      return {
        project_id: this.project.id,
        title: this.form.title,
        description: this.form.description,
        drawing_type: this.form.drawing_type,
        required_date: this.form.required_date,
        associated_documents: this.associatedDocuments,
        installation_order_number: this.toInteger(this.form.installation_order_number),
        quotation_number: this.toInteger(this.form.quotation_number),
        items: this.selectedItems.map((key) => {
          const [itemableType, itemableId] = key.split(':')
          return { itemable_type: itemableType, itemable_id: Number(itemableId) }
        })
      }
    },
    toInteger (value) {
      return value === null || value === '' ? null : parseInt(value, 10)
    },
    photosClass (item) {
      if (item.required_photos_count === 0) return 'text-muted'
      return item.completed_photos_count === item.required_photos_count ? 'text-success' : 'text-danger'
    },
    photosTooltip (item) {
      if (item.required_photos_count === 0) return 'No requiere fotos'

      const { completed_photos_count: completed, required_photos_count: required } = item
      return completed === required
        ? 'Fotos completas'
        : `${completed} de ${required} fotos requeridas`
    },
    onSubmitClick () {
      if (this.incompleteSelectedItems.length > 0) {
        this.confirmIncomplete = true
        return
      }
      this.createDrawingRequest()
    },
    isIncomplete (item) {
      return item.progress < 100 || item.completed_photos_count < item.required_photos_count
    }
  }
}
</script>

<style scoped>
.items-box {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  max-height: 260px;
  overflow-y: auto;
}
.item-row {
  padding: 0.45rem 0.75rem;
  border-bottom: 1px solid #f1f1f1;
  gap: 0.5rem;
}
.item-row:last-child {
  border-bottom: none;
}
.item-check {
  width: 1.75rem;
  flex-shrink: 0;
}
.item-number {
  width: 70px;
  flex-shrink: 0;
  color: #6c757d;
}
.item-title {
  flex: 1;
  min-width: 0;
}
.item-photos {
  width: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}
.item-progress {
  width: 59px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}
.item-count {
  font-variant-numeric: tabular-nums;
}
</style>
